<script setup lang="ts">
import { Entity } from '@/lib/utils'
import httpClient from '@/utils/httpClient'
import { ArrowDownIcon, ArrowUpIcon, EnterIcon, MagnifyingGlassIcon } from '@radix-icons/vue'
import { onKeyStroke, useDebounceFn, useMagicKeys, whenever } from '@vueuse/core'
import { ref } from 'vue'
import { Badge } from './ui/badge'
import { CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command'
import { DialogFooter } from './ui/dialog'
import { useRouter } from '@/composables/router'
import { searchResultsSchema } from '@/lib/schema/v1/search-result'
import { SearchResult, SearchResults } from '@/lib/types/v1'
import { CommandDialog } from '@/components/ui/command'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const selected = defineModel({ type: Object })
const router = useRouter()
const open = ref(false)
const results = ref<SearchResults>([])
const searching = ref(false)
const classes =
    'flex items-center w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-hidden hover:border-blue-500 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 space-x-2 cursor-pointer'

const { ctrl_k, meta_k } = useMagicKeys({
    passive: false,
    onEventFired(e) {
        if (e.type === 'keydown' && (e.ctrlKey || e.metaKey) && e.key === 'k') {
            e.preventDefault()
        }
    },
})

whenever(meta_k, () => {
    open.value = true
})

whenever(ctrl_k, () => {
    open.value = true
})

const handleSearchTermUpdated = useDebounceFn(async (searchTerm?: string) => {
    results.value = []

    if (searchTerm === '' || searchTerm === undefined) {
        return
    }

    searching.value = true

    try {
        const { data } = await httpClient.get('/v1/search', {
            params: { q: searchTerm, limit: 100 },
        })

        results.value = searchResultsSchema.parse(data.data)
    } finally {
        searching.value = false
    }
}, 1000)

onKeyStroke('Escape', () => {
    close()
})

function close() {
    open.value = false
}

function handleSelect(item: SearchResult) {
    if (item.type === Entity.Company) {
        router.goToCompanyDashboard(item.uuid)
    }

    if (item.type === Entity.CompanyService) {
        router.goToCompanyServiceDetails(item.uuid, item.company_uuid)
    }

    if (item.type === Entity.CompanyContact) {
        router.goToCompanyContactDetails(item.uuid, item.company_uuid)
    }

    if (item.type === Entity.Invoice) {
        router.goToCompanyInvoiceDetails(item.uuid, item.company_uuid)
    }

    if (item.type === Entity.CompanyAsset) {
        router.goToCompanyAssetDetails(item.uuid, item.company_uuid)
    }

    if (item.type === Entity.Article) {
        router.goToArticleDetails(item.uuid)
    }

    if (item.type === Entity.Ticket) {
        router.goToTicketDetails(item.uuid)
    }
    close()
}

const openCompanyContactDashboard = (companyId: string) => {
    router.goToCompanyDashboard(companyId)
    open.value = false
}

const icons = {
    [Entity.Company]: 'fal fa-buildings',
    [Entity.CompanyService]: 'fal fa-cubes',
    [Entity.CompanyContact]: 'fal fa-user',
    [Entity.Invoice]: 'fal fa-file-invoice',
    [Entity.CompanyAsset]: 'fal fa-gem',
    [Entity.Article]: 'fal fa-newspaper',
    [Entity.Ticket]: 'fal fa-ticket-alt',
}
</script>

<template>
    <div>
        <div
            class="flex items-center w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-hidden hover:border-blue-500 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 space-x-2 cursor-pointer"
            @click="open = true"
        >
            <MagnifyingGlassIcon class="w-4 h-4 text-muted-foreground shrink-0" />
            <input
                id="global-search"
                type="text"
                placeholder="Search"
                readonly
                style="width: inherit"
                class="cursor-pointer"
            />
            <div class="shrink-0 text-gray-400 text-xs">Ctrl + K</div>
        </div>
        <CommandDialog
            v-model:open="open"
            class="sm:max-w-[725px] p-0 gap-0 top-[20px] translate-y-0"
        >
            <CommandInput
                placeholder="Search"
                :searching="searching"
                @update:model-value="handleSearchTermUpdated"
            />
            <CommandEmpty v-if="results.length === 0">No results found.</CommandEmpty>
            <CommandList class="max-h-[calc(100svh-120px)]">
                <CommandGroup v-if="results.length > 0" class="border-t" as-child>
                    <CommandItem
                        v-for="result in results"
                        :key="result.uuid"
                        :value="result"
                        class="px-2"
                        @select="handleSelect(result)"
                    >
                        <div class="flex flex-1 items-center justify-between space-x-2">
                            <div class="flex items-start space-x-2">
                                <FontAwesomeIcon
                                    :icon="icons[result.type]"
                                    class="shrink-0 size-3! mt-0.5"
                                />
                                <div>
                                    <div class="flex items-center space-x-1">
                                        <span class="text-xs truncate">{{ result.title }}</span>
                                        <span class="text-muted-foreground text-xs truncate">{{
                                            result.name
                                        }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="flex items-center space-x-2 shrink-0">
                                <span class="text-muted-foreground">{{ result.label }}</span>
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger as-child>
                                            <Button
                                                v-if="
                                                    [
                                                        Entity.CompanyContact,
                                                        Entity.CompanyAsset,
                                                        Entity.Ticket,
                                                        Entity.CompanyService,
                                                    ].includes(result.type)
                                                "
                                                variant="icon-white"
                                                size="icon-xs"
                                                type="button"
                                                aria-label="Open contact's company dashboard"
                                                class="cursor-pointer"
                                                @mousedown.stop
                                                @click.stop.prevent="
                                                    openCompanyContactDashboard(result.company_uuid)
                                                "
                                            >
                                                <FontAwesomeIcon
                                                    icon="fal fa-home"
                                                    class="!w-3 !h-3 shrink-0"
                                                />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Go to company dashboard</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                    </CommandItem>
                </CommandGroup>
            </CommandList>
            <DialogFooter class="border-t mt-0">
                <div class="flex items-center text-xs text-gray-500 py-2 px-3 space-x-2">
                    <div class="flex items-center space-x-2">
                        <EnterIcon />
                        <div>to select</div>
                    </div>
                    <div class="flex items-center space-x-2">
                        <div class="flex items-center">
                            <ArrowDownIcon />
                            <ArrowUpIcon />
                        </div>
                        <div>to navigate</div>
                    </div>
                    <div class="flex items-center space-x-2">
                        <Badge variant="outline" class="font-normal">esc</Badge>
                        <div>to close</div>
                    </div>
                </div>
            </DialogFooter>
        </CommandDialog>
    </div>
</template>
